import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import ActionsMenu from "../bundles/shared/components/ActionsMenu";
import "./styles/mySettingsStyles.css";

const axios = require("axios").default;

// Manages the addresses an account answers to. Every row carries a named badge —
// Sign-in address / Confirmed / Waiting for confirmation — because "add an email"
// reads as "add a way in" and it isn't: sign-in and password reset both resolve on
// users.email alone. The badge answers that before anyone has to ask.
//
// The sign-in row has no actions. It changes by promoting another address, which
// swaps the two and keeps both.
const EmailAddressesCard = ({ csrfToken, primaryEmail, addresses }) => {
  const [rows, setRows] = useState(addresses);
  const [newEmail, setNewEmail] = useState("");
  // Two error channels, because they belong in different places: a rejected
  // address is a fault in the field you just typed in, and everything else answers
  // a button several rows away.
  const [addError, setAddError] = useState(null);
  const [alert, setAlert] = useState(null);
  const [pendingRemoval, setPendingRemoval] = useState(null);
  const [pendingPromotion, setPendingPromotion] = useState(null);
  const [busy, setBusy] = useState(false);
  const alertRef = useRef(null);

  const openModal = pendingRemoval || pendingPromotion;
  // The overlay is fixed and full-screen, so it already swallows mouse clicks — but
  // the rows behind it keep their tab order, and Tabbing past Cancel reached Remove
  // on a DIFFERENT address. Disabling them takes them out of the tab order too.
  // Not a focus trap: aria-modal here is still a promise the markup doesn't keep,
  // and closing that gap belongs with the app-wide a11y work rather than a third
  // hand-rolled dialog. The confirm buttons inside the modals stay on `busy`.
  const locked = busy || !!openModal;

  useEffect(() => {
    if (!openModal) return undefined;
    const onKeyDown = (e) => {
      if (e.key !== "Escape") return;
      setPendingRemoval(null);
      setPendingPromotion(null);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [openModal]);

  // Resend and Make sign-in address are triggered from rows below this banner, and
  // at the ten-address cap the card is taller than the viewport — so the answer to
  // a button press can land off-screen.
  useEffect(() => {
    if (!alert) return;
    if (!alertRef.current) return;
    if (!alertRef.current.scrollIntoView) return;
    alertRef.current.scrollIntoView({ block: "nearest" });
  }, [alert]);

  axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;

  const failureMessage = (e, fallback) => {
    const errors = e && e.response && e.response.data && e.response.data.errors;
    return errors && errors.length ? errors.join(", ") : fallback;
  };

  const succeeded = (title, desc) => setAlert({ tone: "success", title, desc });
  const failed = (title) => setAlert({ tone: "error", title });

  const startRequest = () => {
    setBusy(true);
    setAddError(null);
    setAlert(null);
  };

  const addEmail = (e) => {
    e.preventDefault();
    if (busy || newEmail.trim().length === 0) return;

    startRequest();

    axios.post("/user_emails", { user_email: { email: newEmail } })
      .then((response) => {
        setRows(rows.concat([response.data]));
        setNewEmail("");
        // The address is saved either way, but the mail is not guaranteed: sending
        // runs after commit and refuses once that address has burnt its daily
        // budget. Claiming we sent a link then sends the user to an empty inbox, so
        // point them at Resend instead.
        if (response.data.verification_sent) {
          succeeded(
            `Confirmation email sent to ${response.data.email}`,
            <>Open it and press <strong>Confirm this address</strong>. Nothing is added to your account until you do.</>
          );
        } else {
          failed(
            `Added ${response.data.email}, but we could not send a confirmation link right now. Use Resend in a little while.`
          );
        }
      })
      // On the field, not the banner: every rejection here — already in use, over
      // the ten-address cap, malformed — is about the address still sitting in the
      // input.
      .catch((e) => setAddError(failureMessage(e, "We could not add that email address.")))
      .then(() => setBusy(false));
  };

  const resend = (row) => {
    if (busy) return;

    startRequest();

    axios.post(`/user_emails/${row.id}/verification`)
      .then((response) => {
        setRows(rows.map((candidate) => (candidate.id === row.id ? response.data : candidate)));
        // True because the verification token is keyed on verification_sent_at:
        // a resend re-mints it and every link already mailed stops resolving.
        succeeded(
          `New confirmation link sent to ${row.email}`,
          "The previous link stopped working the moment this one went out."
        );
      })
      // The server refuses a second link inside RESEND_INTERVAL, and again once the
      // address has burnt its daily budget. Both come back worded, and both have to
      // be said out loud — a button that silently does nothing reads as broken.
      .catch((e) => failed(failureMessage(e, "We could not send that confirmation email.")))
      .then(() => setBusy(false));
  };

  // Reloads rather than patching state: promoting swaps the sign-in address, which
  // the profile form higher up the page also displays.
  const makePrimary = (row) => {
    startRequest();

    axios.post(`/user_emails/${row.id}/make_primary`)
      .then(() => window.location.reload())
      .catch((e) => {
        failed(failureMessage(e, "We could not make that your sign-in address."));
        setPendingPromotion(null);
        setBusy(false);
      });
  };

  // Closes the modal on failure as well as success. The overlay is fixed and
  // full-screen, so an error left behind it is painted over — the button would
  // just look dead. Reachable whenever the row is already gone, e.g. a second
  // tab removed it.
  const remove = (row) => {
    startRequest();

    axios.delete(`/user_emails/${row.id}`)
      .then(() => {
        setRows(rows.filter((candidate) => candidate.id !== row.id));
        // Only the tickets half of that is true: ticket ownership is materialized in
        // ticket_ownerships, while memberships, passes and donations all resolve by
        // matching the address through User#all_emails and stop showing up.
        succeeded(`Removed ${row.email}`, "Tickets already in your account stay put.");
      })
      .catch((e) => failed(failureMessage(e, "We could not remove that email address.")))
      .then(() => {
        setPendingRemoval(null);
        setBusy(false);
      });
  };

  const menuTrigger = <i className="fal fa-ellipsis-h" style={{ fontSize: "16px" }} />;

  const removeAction = (row, dividerBefore = false) => ({
    label: "Remove address",
    icon: "fal fa-trash-alt",
    danger: true,
    disabled: locked,
    dividerBefore,
    onClick: () => setPendingRemoval(row),
  });

  const addressRow = ({ key, email, status, statusTone, meta, actions }) => (
    <li className="settings-addr-row" key={key}>
      <span className="settings-addr-main">
        <span className="settings-addr-top">
          <span className="settings-addr-email">{email}</span>
          <span className={`settings-status settings-status--${statusTone}`}>{status}</span>
        </span>
        <span className="settings-addr-meta">{meta}</span>
      </span>
      <span className="settings-addr-actions">{actions}</span>
    </li>
  );

  // The row says when the link went out, because whether to press Resend again
  // depends on it. A row with nothing sent is the daily-budget refusal, and saying
  // "sent, check your inbox" there points at an empty inbox.
  const pendingMeta = (row) => (
    row.verification_sent_ago
      ? `Confirmation email sent ${row.verification_sent_ago} ago. The link expires 24 hours after you added the address.`
      : "We haven't been able to send a confirmation email yet — try Resend in a little while."
  );

  return (
    <>
      {/* role="status" on the successes: Resend changes no visible row, so without
          it a screen reader gets silence. */}
      {alert ? (
        <div
          className="settings-alert"
          ref={alertRef}
          role={alert.tone === "error" ? "alert" : "status"}
        >
          <span
            className={`settings-alert-icon ${alert.tone === "error" ? "is-error" : "is-success"}`}
            aria-hidden="true"
          >
            <i className={alert.tone === "error" ? "fal fa-exclamation-circle" : "fal fa-check-circle"} />
          </span>
          <span className="settings-alert-content">
            <span className="settings-alert-title">{alert.title}</span>
            {alert.desc ? <span className="settings-alert-desc">{alert.desc}</span> : null}
          </span>
          <button
            type="button"
            className="settings-alert-close"
            aria-label="Dismiss"
            onClick={() => setAlert(null)}
          >
            <i className="fal fa-times" aria-hidden="true" />
          </button>
        </div>
      ) : null}

      <section className="settings-card" data-settings-section id="email-addresses">
        <div className="settings-card-header">
          <h2 className="settings-card-title">Email addresses</h2>
          <p className="settings-card-desc">
            Add every address you've used to buy tickets and they'll all show up in one place.
          </p>
        </div>

        <div className="settings-card-body">
          <ul className="settings-addr-list">
            {addressRow({
              key: "primary",
              email: primaryEmail,
              status: "Sign-in address",
              statusTone: "brand",
              meta: "You sign in with this address.",
            })}

            {rows.map((row) => (
              row.verified
                ? addressRow({
                  key: row.id,
                  email: row.email,
                  status: "Confirmed",
                  statusTone: "success",
                  meta: "Tickets bought with this address show up in your account.",
                  actions: (
                    <ActionsMenu
                      triggerIcon={menuTrigger}
                      actions={[
                        {
                          label: "Make this my sign-in address",
                          icon: "fal fa-sign-in",
                          disabled: locked,
                          onClick: () => setPendingPromotion(row),
                        },
                        removeAction(row, true),
                      ]}
                    />
                  ),
                })
                : addressRow({
                  key: row.id,
                  email: row.email,
                  status: "Waiting for confirmation",
                  statusTone: "warning",
                  meta: pendingMeta(row),
                  actions: (
                    <>
                      <button
                        type="button"
                        className="settings-btn settings-btn-secondary"
                        aria-label={`Resend the confirmation to ${row.email}`}
                        disabled={locked}
                        onClick={() => resend(row)}
                      >
                        <i className="fal fa-redo" aria-hidden="true" />
                        Resend
                      </button>
                      <ActionsMenu triggerIcon={menuTrigger} actions={[removeAction(row)]} />
                    </>
                  ),
                })
            ))}
          </ul>

          <form onSubmit={addEmail} className="settings-addr-add">
            <div className="settings-field">
              <label className="settings-label" htmlFor="user-email-new">Add an email address</label>
              <input
                type="email"
                className={`form-control settings-input${addError ? " has-error" : ""}`}
                id="user-email-new"
                placeholder="you@example.com"
                autoComplete="email"
                aria-describedby="user-email-new-help"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
              />
              {addError ? (
                <p className="settings-field-error" id="user-email-new-help" role="alert">{addError}</p>
              ) : (
                <p className="settings-helper" id="user-email-new-help">
                  We'll email this address to confirm it's yours. Nothing changes until you confirm.
                </p>
              )}
            </div>
            <button
              type="submit"
              className="settings-btn"
              disabled={locked || newEmail.trim().length === 0}
            >
              <i className="fal fa-plus" aria-hidden="true" />
              Add address
            </button>
          </form>
        </div>
      </section>

      {pendingPromotion ? (
        <div className="settings-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="promote-email-title">
          <div className="settings-modal">
            <h3 className="settings-modal-title" id="promote-email-title">
              Make {pendingPromotion.email} your sign-in address?
            </h3>
            <p className="settings-modal-body">
              You'll sign in with {pendingPromotion.email} from now on, and password resets
              will go there. {primaryEmail} stays on your account and keeps finding tickets
              bought with it.
            </p>
            <div className="settings-modal-actions">
              <button
                type="button"
                className="settings-btn settings-btn-secondary"
                autoFocus
                onClick={() => setPendingPromotion(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="settings-btn"
                disabled={busy}
                onClick={() => makePrimary(pendingPromotion)}
              >
                Make sign-in address
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {pendingRemoval ? (
        <div className="settings-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="remove-email-title">
          <div className="settings-modal">
            <h3 className="settings-modal-title" id="remove-email-title">
              Remove {pendingRemoval.email}?
            </h3>
            <p className="settings-modal-body">
              Memberships, passes and donations bought with this address will stop showing
              up in your account. Tickets already in your account stay put. You can add
              the address again later.
            </p>
            <div className="settings-modal-actions">
              <button
                type="button"
                className="settings-btn settings-btn-secondary"
                autoFocus
                onClick={() => setPendingRemoval(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="settings-btn settings-btn-error"
                disabled={busy}
                onClick={() => remove(pendingRemoval)}
              >
                Remove address
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

EmailAddressesCard.propTypes = {
  csrfToken: PropTypes.string.isRequired,
  primaryEmail: PropTypes.string.isRequired,
  addresses: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    email: PropTypes.string.isRequired,
    verified: PropTypes.bool.isRequired,
    verification_sent_ago: PropTypes.string,
  })).isRequired,
};

export default EmailAddressesCard;
